(() => {
  const base = 'https://informationcollection.benyuan1.com/api/';
  const canSubmit = (activity, now = Date.now()) => {
    const deadline = Number(activity?.submitEnd);
    return activity?.canSubmit === true && (!Number.isFinite(deadline) || deadline <= 0 || now < deadline);
  };
  const pacificTimeZone = 'America/Los_Angeles';
  let pacificClock;
  const pacificWallTime = timestamp => {
    pacificClock ||= new Intl.DateTimeFormat('en-US', {
      timeZone: pacificTimeZone, calendar: 'gregory', numberingSystem: 'latn',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    });
    const fields = Object.fromEntries(pacificClock.formatToParts(timestamp).map(part => [part.type, part.value]));
    const wall = new Date(0);
    wall.setUTCFullYear(Number(fields.year), Number(fields.month) - 1, Number(fields.day));
    wall.setUTCHours(Number(fields.hour), Number(fields.minute), Number(fields.second), 0);
    return wall.getTime();
  };
  const parseCountdownDeadline = value => {
    // Plain theme-editor input is Los Angeles time, never the visitor's local time.
    // Keep explicit offsets compatible with previously saved ISO settings.
    const parts = String(value ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:\d{2})?$/i);
    if (!parts) return null;
    const [year, month, day, hour, minute, second] = parts.slice(1, 7).map(part => Number(part || 0));
    const local = new Date(0);
    local.setUTCFullYear(year, month - 1, day);
    local.setUTCHours(hour, minute, second, Number((parts[7] || '').padEnd(3, '0')));
    // Date normalizes invalid dates such as February 30; reject those inputs.
    if (local.getUTCFullYear() !== year || local.getUTCMonth() !== month - 1 || local.getUTCDate() !== day || local.getUTCHours() !== hour || local.getUTCMinutes() !== minute || local.getUTCSeconds() !== second) return null;
    if (!parts[8]) {
      const wallTime = local.getTime(), wallSecond = wallTime - local.getUTCMilliseconds();
      // Discover the offsets on both sides of a possible daylight-saving change.
      // Round-trip candidates to reject skipped times; for repeated times use the
      // first occurrence. An explicit ISO offset can select the second occurrence.
      const offsets = new Set([-86400000, 86400000].map(delta => {
        const probe = wallSecond + delta;
        return pacificWallTime(probe) - probe;
      }));
      const candidates = [...offsets].map(offset => wallTime - offset)
        .filter(deadline => deadline > 0 && pacificWallTime(deadline) === wallSecond);
      return candidates.length ? Math.min(...candidates) : null;
    }
    const zone = parts[8].toUpperCase();
    let offset = 0;
    if (zone !== 'Z') {
      const hours = Number(zone.slice(1, 3)), minutes = Number(zone.slice(4, 6));
      if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) return null;
      offset = (hours * 60 + minutes) * 60000 * (zone[0] === '+' ? 1 : -1);
    }
    const deadline = local.getTime() - offset;
    return Number.isFinite(deadline) && deadline > 0 ? deadline : null;
  };
  const formatPacificDeadline = deadline => {
    if (!Number.isFinite(deadline) || deadline <= 0) return '';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: pacificTimeZone, year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZoneName: 'short'
    }).format(deadline);
  };
  const countdownParts = (deadline, now = Date.now()) => {
    const end = Number(deadline);
    if (!Number.isFinite(end) || end <= 0) return null;
    const remainingMs = Math.max(0, end - now), totalSeconds = Math.ceil(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400), hours = Math.floor(totalSeconds / 3600) % 24;
    const minutes = Math.floor(totalSeconds / 60) % 60, seconds = totalSeconds % 60;
    const underHour = remainingMs < 3600000;
    return { remainingMs, days, hours, minutes, seconds, mobileMode: underHour ? 'minutes-seconds' : 'days-hours', mobileFirst: underHour ? Math.floor(totalSeconds / 60) : days, mobileSecond: underHour ? seconds : hours };
  };
  const challengeShareUrl = href => {
    const url = new URL(href);
    url.searchParams.delete('content'); url.searchParams.delete('join_challenge'); url.hash = '';
    return url.href;
  };
  const listPayload = (activityId, page, sortBy, userId, size = 12) => {
    if (!activityId) throw Error('Select a challenge.');
    return { type: 'CHALLENGE', activityId: String(activityId), start: page, size, sortBy, sortOrder: 'desc', ...(userId ? { userId: String(userId) } : {}) };
  };
  const buildEntry = (entry, activity, userId) => {
    if (!userId) throw Error('Please log in before posting.');
    if (!canSubmit(activity) || String(entry.activityId) !== String(activity.id)) throw Error('Submissions are closed for this challenge.');
    if (!activity.roles?.includes(entry.role)) throw Error('Select a role available for this challenge.');
    const title = String(entry.title || '').trim(), content = String(entry.content || '').trim();
    if (!title || title.length > 200 || content.length > 20000 || !entry.nailShape || !entry.nailLength) throw Error('Check your title, content, nail shape and length.');
    if (typeof entry.userAuthorization !== 'boolean') throw Error('Choose your authorization preference.');
    const media = (entry.media || []).map(({ type, url }) => {
      let parsed;
      try { parsed = new URL(url); } catch {}
      if (!['image', 'video'].includes(type) || parsed?.protocol !== 'https:' || parsed.username || parsed.password) throw Error('Complete all media uploads.');
      return { type, url };
    });
    const images = media.filter(item => item.type === 'image').length;
    if (!media.length || images > 9) throw Error('Add at least one photo or video (up to 9 photos).');
    return { userId: String(userId), type: 'CHALLENGE', activityId: String(activity.id), ...(activity.campaignTag ? { activityTag: activity.campaignTag } : {}), role: entry.role, title, content, nailShape: entry.nailShape, nailLength: entry.nailLength, skinTone: entry.skinTone || '', contentLicense: Boolean(entry.contentLicense), userAuthorization: entry.userAuthorization, media, products: entry.products || [] };
  };
  const votePayload = (activity, item, userId, requestId) => {
    if (!userId || !activity?.canVote || !['CURRENT', 'HISTORY'].includes(activity.status) || String(item.activityId) !== String(activity.id)) throw Error('Voting is not available for this entry.');
    return { userId: String(userId), challengeId: String(activity.id), entryId: String(item.id), scene: activity.status === 'HISTORY' ? 'history' : 'current', requestId };
  };
  if (typeof module !== 'undefined') module.exports = { listPayload, buildEntry, votePayload, canSubmit, parseCountdownDeadline, formatPacificDeadline, countdownParts, challengeShareUrl, userBadgeKeys: require('./community-feed.js').userBadgeKeys };
  if (typeof window === 'undefined' || window.MonthlyChallenge) return;
  const el = (tag, cls, text) => { const node = document.createElement(tag); if (cls) node.className = cls; if (text !== undefined) node.textContent = text; return node; };
  const count = value => Math.max(0, Number(value) || 0);
  const init = async root => {
    if (root.dataset.challengeReady) return;
    root.dataset.challengeReady = 'true';
    const life = new AbortController();
    let disposed = false;
    const { safeUrl, mediaUrl, readPage, withUserIdentity, userDisplayName } = window.CommunityFeedUtils;
    const userId = root.dataset.customerId;
    const countdownDeadline = parseCountdownDeadline(root.dataset.countdownEndAt);
    const pacificDeadline = formatPacificDeadline(countdownDeadline);
    const officialEmails = window.CommunityFeedUtils.officialEmailSet(root.dataset.officialEmails);
    const query = selector => root.querySelector(selector);
    const status = query('[data-challenge-status]'), grid = query('[data-challenge-list]'), pages = query('[data-feed-pages]');
    const selector = query('[data-activity-select]'), activityStatus = query('[data-activity-status]');
    let activity = null, page = 1, sort = 'favoriteCount', serial = 0, activitySerial = 0, productCleanups = [], countdownTimer = null;
    const pendingVotes = new Map(), activeVotes = new Set();
    const mobile = matchMedia('(max-width: 749px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const pageSize = () => mobile.matches ? 6 : 12;
    const stopCountdown = () => { if (countdownTimer !== null) clearTimeout(countdownTimer); countdownTimer = null; };
    const settleCountdownFlips = () => root.querySelectorAll('.monthly-challenge__flip-digit.is-flipping').forEach(digit => digit.classList.remove('is-flipping'));
    const writeCountdownDigit = (digit, value, animate) => {
      const previous = digit.dataset.value;
      if (previous === value) return;
      if (previous === undefined) {
        digit.classList.add('monthly-challenge__flip-digit');
        digit.replaceChildren(el('span', 'monthly-challenge__flip-value'));
      }
      digit.classList.remove('is-flipping');
      digit.dataset.previous = previous ?? value;
      digit.dataset.value = value;
      const face = digit.firstElementChild;
      face.dataset.previous = previous ?? value;
      face.dataset.value = value;
      face.textContent = value;
      if (previous !== undefined && animate && !document.hidden && !reducedMotion.matches) digit.classList.add('is-flipping');
    };
    const renderCountdown = () => {
      stopCountdown();
      const countdown = query('[data-challenge-countdown]');
      if (!countdown || disposed) return;
      const remaining = activity?.status === 'CURRENT' && countdownParts(countdownDeadline);
      countdown.hidden = !remaining;
      if (!remaining) return;
      const writeUnit = (selector, value, animate) => {
        const node = countdown.querySelector(selector);
        if (!node) return;
        const digits = String(value).padStart(2, '0');
        writeCountdownDigit(node.children[0], digits.slice(0, -1), animate);
        writeCountdownDigit(node.children[1], digits.slice(-1), animate);
      };
      for (const unit of ['days', 'hours', 'minutes', 'seconds']) writeUnit(`[data-countdown-desktop] [data-countdown-unit="${unit}"]`, remaining[unit], !mobile.matches);
      const compact = countdown.querySelector('[data-countdown-mobile]');
      if (compact) {
        const sameUnits = compact.dataset.countdownMode === remaining.mobileMode;
        compact.dataset.countdownMode = remaining.mobileMode;
        writeUnit('[data-countdown-mobile] [data-countdown-unit="first"]', remaining.mobileFirst, mobile.matches && sameUnits);
        writeUnit('[data-countdown-mobile] [data-countdown-unit="second"]', remaining.mobileSecond, mobile.matches && sameUnits);
      }
      const artwork = countdown.querySelector('[data-countdown-source]');
      if (artwork) {
        const compactTime = remaining.mobileMode === 'minutes-seconds';
        const source = compactTime ? artwork.dataset.minutesSrc : artwork.dataset.daysSrc;
        if (artwork.getAttribute('srcset') !== source) artwork.setAttribute('srcset', source);
        artwork.width = compactTime ? 2112 : 2064;
        artwork.height = compactTime ? 745 : 762;
        countdown.dataset.countdownMode = remaining.mobileMode;
      }
      countdown.setAttribute('aria-label', remaining.remainingMs ? `Voting ends in ${remaining.days} days, ${remaining.hours} hours, ${remaining.minutes} minutes and ${remaining.seconds} seconds. Deadline: ${pacificDeadline} (Pacific Time).` : 'Voting closed.');
      if (remaining.remainingMs && !document.hidden) countdownTimer = setTimeout(renderCountdown, Math.min(1000, remaining.remainingMs));
    };
    const metric = (control, kind, value, label) => {
      const icon = query('[data-challenge-icons]').content.querySelector(`[data-icon="${kind}"]`).cloneNode(true);
      control.replaceChildren(icon, document.createTextNode(String(value)));
      control.setAttribute('aria-label', `${label} (${value})`);
    };
    const api = async (path, payload, signal = life.signal) => {
      const identity = payload.userId && String(payload.userId) === String(userId) ? await window.CommunityFeedUtils.resolveIdentity({ userId, email: root.dataset.customerEmail, displayName: root.dataset.customerName }) : {};
      const response = await fetch(`${base}community/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withUserIdentity(payload, identity)), signal: AbortSignal.any([signal, life.signal, AbortSignal.timeout(20000)]) });
      const body = await response.json();
      if (!response.ok || body.status !== 'success') throw Error(body.message || 'Unable to complete this request. Please retry.');
      if (path === 'content/detail' && (body.data?.type !== 'CHALLENGE' || !body.data?.activityId)) throw Error('This challenge entry is unavailable.');
      return body;
    };
    const login = () => window.CommunityRequireLogin(root);
    const mediaNode = (media, cls, title) => {
      const url = mediaUrl(media?.url); if (!url || !['image', 'video'].includes(media.type)) return null;
      const node = el(media.type === 'image' ? 'img' : 'video', cls); node.src = url;
      if (media.type === 'image') { node.alt = title || ''; node.loading = 'lazy'; }
      else { node.controls = true; node.playsInline = true; node.preload = 'metadata'; }
      return node;
    };
    const avatar = (node, user) => {
      node.textContent = userDisplayName(user, '?').slice(0, 1);
      const url = mediaUrl(user?.avatar); if (url) { const image = el('img'); image.src = url; image.alt = ''; node.replaceChildren(image); }
      window.CommunityFeedUtils.renderProAvatar(node, user);
    };
    const button = (label, action) => { const node = el('button', '', label); node.type = 'button'; node.addEventListener('click', action); return node; };
    const shareContent = window.CommunityShareMenu(root, { api, login, userId, el, life });
    const voteButton = (item, selected, message) => {
      const vote = button('', async () => {
        if (!login() || activeVotes.has(item.id)) return;
        vote.disabled = true; activeVotes.add(item.id);
        // An uncertain request keeps its ID even if the card is re-rendered. Only a
        // confirmed response permits the next deliberate vote to use a new ID.
        const requestId = pendingVotes.get(item.id) || crypto.randomUUID(); pendingVotes.set(item.id, requestId);
        try {
          const { data } = await api('challenge/vote', votePayload(selected, item, userId, requestId));
          if (!Number.isInteger(data?.voteCount) || typeof data.counted !== 'boolean') throw Error('Vote confirmation was not received. Retry to check this vote.');
          pendingVotes.delete(item.id); item.voteCount = data.voteCount;
          message.textContent = data.counted ? 'Vote counted. You can vote again.' : 'This vote was already counted.';
          sync();
        } catch (error) { message.textContent = error.message; vote.textContent = 'Retry vote'; }
        finally { activeVotes.delete(item.id); vote.disabled = !selected?.canVote; }
      });
      const sync = () => { vote.textContent = `${selected?.canVote ? 'Vote' : 'Voting closed'} (${count(item.voteCount)})`; vote.disabled = !selected?.canVote; };
      vote.dataset.challengeVote = item.id; sync(); return vote;
    };
    const reactButton = (item, kind, message, compact = false) => {
      const favorite = kind === 'favorite', field = favorite ? 'isFavorited' : 'isLiked';
      const control = button('', async () => {
        if (!login()) return; control.disabled = true;
        try {
          const { data } = await api(`reaction/${kind}`, { userId, contentType: 'challenge', contentId: item.id, [field]: !item[field] });
          if (typeof data?.[field] !== 'boolean') throw Error('Unable to confirm this action.');
          Object.assign(item, data); sync();
        } catch (error) { message.textContent = error.message; }
        finally { control.disabled = false; }
      });
      const sync = () => { control.textContent = `${favorite ? (item.isFavorited ? 'Saved' : 'Save') : 'Like'} (${count(item[favorite ? 'favoriteCount' : 'likeCount'])})`; control.setAttribute('aria-pressed', String(Boolean(item[field]))); if (compact) metric(control, 'like', count(item[favorite ? 'favoriteCount' : 'likeCount']), favorite ? 'Save' : 'Like'); };
      sync(); return control;
    };
    const detail = window.CommunityFeedDetail(root, { api, login, userId, el, avatar, mediaNode, mediaUrl, safeUrl, life, shareContent,
      extendActions: async ({ item, actions, status: message, isCurrent }) => {
        const primary = Array.from(actions.children);
        actions.replaceChildren(primary[1], primary[0], primary[2]);
        try {
          const { data } = await api('challenge/activity', { activityId: item.activityId });
          if (!isCurrent() || disposed) return;
          if (String(activity?.id) !== String(item.activityId)) await selectActivity(item.activityId);
        } catch (error) { if (isCurrent()) message.textContent = error.message; }
      }
    });
    const card = item => {
      const node = query('[data-feed-template="POST"]').content.firstElementChild.cloneNode(true);
      node.dataset.contentId = item.id;
      const url = new URL(location.href); url.searchParams.set('content', item.id);
      const open = event => { if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button)) return; event?.preventDefault(); detail.open(item.id, 'CHALLENGE', item); };
      const title = node.querySelector('h3 a'); title.textContent = item.title; title.href = url.href; title.addEventListener('click', open);
      const old = node.querySelector('.community-share__media'), cover = el('div', old.className);
      const image = mediaNode(item.media?.find(media => media.type === 'image') || item.media?.[0], '', item.title); if (image) cover.append(image);
      if (item.role) cover.append(el('span', 'community-share__role-tag', item.role));
      old.replaceWith(cover); cover.addEventListener('click', open);
      node.querySelector('.community-share__author strong').textContent = userDisplayName(item.user);
      node.querySelector('.community-share__author small').textContent = (Array.isArray(item.user?.publicTags) ? item.user.publicTags : []).join(' · ');
      avatar(node.querySelector('.community-share__avatar'), item.user);
      window.CommunityFeedUtils.renderOfficialBadge(node.querySelector('.community-share__author-row'), item.user, officialEmails);
      const renderUserBadges = user => window.CommunityFeedUtils.renderUserBadges(node, user);
      renderUserBadges(item.user);
      if (item.user?.userId && (item.user.award === undefined || (item.user.points === undefined && item.user.levelBadge === undefined))) {
        window.CommunityFeedUtils.resolveIdentity(item.user).then(user => {
          if (!disposed && node.isConnected) renderUserBadges(user);
        }).catch(() => {});
      }
      const shop = node.querySelector('.community-share__shop');
      productCleanups.push(window.CommunityCardProducts(shop, item.products, { el, safeUrl, mediaUrl, life }));
      const actions = node.querySelector('.community-share__metrics');
      const like = reactButton(item, 'favorite', status, true);
      const share = button('', () => {}); metric(share, 'share', count(item.shareCount), 'Share');
      shareContent.bindTrigger(share, item, 'CHALLENGE', data => { item.shareCount = data.shareCount; metric(share, 'share', count(item.shareCount), 'Share'); });
      const comments = button('', () => { if (login()) detail.open(item.id, 'CHALLENGE', item, { scrollToComments: true }); }); metric(comments, 'comments', count(item.commentCount), 'Comments');
      actions.replaceChildren(like, comments, share);
      return node;
    };
    const skeleton = () => Array.from({ length: pageSize() }, () => {
      const node = el('article', 'community-share__card community-feed__post-skeleton'); node.setAttribute('aria-hidden', 'true');
      const block = name => el('span', `community-feed__skeleton-block community-feed__skeleton-${name}`);
      const copy = el('div', 'community-feed__post-skeleton-content'); copy.append(block('name'), block('title'), block('line'), block('short'), block('action'));
      node.append(block('post-cover'), copy); return node;
    });
    const showListLoading = () => {
      status.textContent = ''; pages.replaceChildren();
      query('[data-challenge-retry]').hidden = true;
      grid.setAttribute('aria-busy', 'true'); grid.replaceChildren(...skeleton());
    };
    const clearListLoading = () => { grid.replaceChildren(); grid.removeAttribute('aria-busy'); };
    const load = async ({ scrollToTop = false } = {}) => {
      const current = ++serial;
      productCleanups.forEach(cleanup => cleanup()); productCleanups = [];
      pages.replaceChildren(); status.textContent = ''; query('[data-challenge-retry]').hidden = true;
      if (!activity) { grid.replaceChildren(); grid.removeAttribute('aria-busy'); return; }
      showListLoading();
      if (scrollToTop) grid.scrollIntoView({ block: 'start', behavior: 'instant' });
      try {
        const size = pageSize();
        const [body] = await Promise.all([api('content/list', listPayload(activity.id, page, sort, userId, size)), new Promise(resolve => setTimeout(resolve, 220))]);
        if (current !== serial || disposed) return;
        const result = readPage(body);
        const items = result.list.filter(item => item.type === 'CHALLENGE' && String(item.activityId) === String(activity.id) && !item.deleted && item.published !== false);
        grid.replaceChildren(...items.map(card));
        if (!items.length) status.textContent = 'No entries yet for this challenge.';
        const total = Math.ceil(result.totalCount / size);
        if (total > 1) {
          const prev = button('Previous', () => { page--; load({ scrollToTop: true }); }); prev.disabled = page <= 1;
          const next = button('Next', () => { page++; load({ scrollToTop: true }); }); next.disabled = page >= total;
          pages.append(prev);
          const numbers = [...new Set([1, page - 1, page, page + 1, total])].filter(n => n > 0 && n <= total).sort((a, b) => a - b);
          numbers.forEach((number, index) => {
            if (index && number - numbers[index - 1] > 1) pages.append(el('span', '', '…'));
            const control = button(String(number), () => { if (page === number) return; page = number; load({ scrollToTop: true }); });
            control.setAttribute('aria-label', `Page ${number}`); if (number === page) control.setAttribute('aria-current', 'page');
            pages.append(control);
          });
          prev.textContent = '‹'; prev.setAttribute('aria-label', 'Previous'); next.textContent = '›'; next.setAttribute('aria-label', 'Next'); pages.append(next);
        }
      } catch (error) { if (current === serial && !disposed) { grid.replaceChildren(); status.textContent = error.message; query('[data-challenge-retry]').hidden = false; } }
      finally { if (current === serial) grid.removeAttribute('aria-busy'); }
    };
    await customElements.whenDefined('monthly-challenge-form');
    const form = query('monthly-challenge-form');
    form.config.loggedIn = Boolean(userId);
    const showActivity = (data, loading = false) => {
      stopCountdown();
      activity = data; form.setActivity(activity);
      const results = query('[data-challenge-results]');
      if (results) results.hidden = Boolean(activity && activity.status !== 'CURRENT');
      root.querySelectorAll('[data-monthly-entry-open]:not([data-challenge-cover]), monthly-challenge-form [data-open], [data-challenge-invite]').forEach(control => {
        control.disabled = !activity?.id;
        if (loading) control.setAttribute('aria-busy', 'true');
        else control.removeAttribute('aria-busy');
      });
      const cover = query('[data-challenge-cover]'); cover.replaceChildren(); cover.hidden = !loading;
      cover.classList.toggle('is-loading', loading); cover.toggleAttribute('aria-busy', loading);
      if (loading) cover.setAttribute('aria-busy', 'true');
      if (!activity) return;
      const configured = activity.status === 'CURRENT' && query('[data-challenge-hero]').content;
      const image = mediaNode({ type: 'image', url: activity.coverImage }, '', activity.name);
      if (configured) { cover.append(configured.cloneNode(true)); cover.hidden = false; }
      else if (image) { image.loading = 'eager'; cover.append(image); cover.hidden = false; }
      renderCountdown();
      const heroImages = [...cover.querySelectorAll('img')];
      const heroImage = heroImages[0];
      if (!heroImage) return;
      cover.classList.add('is-loading'); cover.setAttribute('aria-busy', 'true');
      const finish = () => {
        if (disposed || !cover.contains(heroImage)) return;
        if (!heroImage.complete) return;
        if (heroImage.naturalWidth && heroImages.some(image => !image.complete)) return;
        cover.classList.remove('is-loading'); cover.removeAttribute('aria-busy');
        cover.hidden = !heroImage.naturalWidth;
      };
      heroImages.forEach(image => {
        image.addEventListener('load', finish, { once: true, signal: life.signal });
        image.addEventListener('error', finish, { once: true, signal: life.signal });
      });
      if (heroImage.complete) finish();
    };
    form.adapter = {
      getActivity: async (activityId, { signal }) => {
        const { data } = await api('challenge/activity', { activityId }, signal);
        if (!data || String(data.id) !== String(activityId)) throw Error('This challenge is unavailable.');
        return data;
      },
      uploadMedia: async ({ file }, { signal }) => {
        const data = new FormData(); data.append('file', file); data.append('bizCode', 'community');
        const response = await fetch(`${base}attachment/upload`, { method: 'POST', body: data, signal: AbortSignal.any([signal, life.signal, AbortSignal.timeout(120000)]) });
        const body = await response.json(), url = mediaUrl(body.data?.url);
        if (!response.ok || body.status !== 'success' || !url?.startsWith('https://')) throw Error(body.message || 'Upload failed. Please retry.');
        return { url };
      },
      createEntry: async (entry, { signal }) => {
        const { data: current } = await api('challenge/activity', { activityId: entry.activityId }, signal);
        const payload = buildEntry(entry, current, userId);
        const { data } = await api('content/submit', payload, signal);
        if (!data?.id || data.published !== true) throw Error(data?.message || 'Publication was not confirmed.');
        return data;
      }
    };
    const selectActivity = async id => {
      const current = ++activitySerial; serial++;
      showActivity(null, true); showListLoading();
      activityStatus.textContent = ''; query('[data-activity-retry]').hidden = true;
      try {
        const { data } = await api('challenge/activity', id ? { activityId: id } : {});
        if (current !== activitySerial || disposed) return;
        if (id && (!data || String(data.id) !== String(id))) throw Error('This challenge is unavailable.');
        showActivity(data); page = 1;
        activityStatus.textContent = data ? '' : 'No current challenge. Choose a past challenge to view entries.';
        if (data) selector.value = data.id;
        await load();
      } catch (error) { if (current === activitySerial && !disposed) { showActivity(null); clearListLoading(); activityStatus.textContent = error.message; query('[data-activity-retry]').hidden = false; } }
    };
    const start = async ({ loadCurrent = true } = {}) => {
      showActivity(null, true);
      if (loadCurrent) showListLoading();
      selector.disabled = true; activityStatus.textContent = ''; query('[data-activity-retry]').hidden = true;
      try {
        const { data } = await api('challenge/activities', {});
        if (disposed) return;
        if (!Array.isArray(data)) throw Error('Unable to load challenges.');
        selector.replaceChildren(new Option('Select a challenge', ''), ...data.map(item => new Option(`${item.status === 'CURRENT' ? 'Current' : 'Past'} · ${item.name}${item.month ? ` (${item.month})` : ''}`, item.id)));
        selector.disabled = data.length === 0;
        query('[data-activity-picker]').hidden = data.length < 2 && !data.some(item => item.status === 'HISTORY');
        if (activity?.id) selector.value = activity.id;
        if (loadCurrent) await selectActivity();
      } catch (error) { if (!disposed) { if (loadCurrent) { showActivity(null); clearListLoading(); } activityStatus.textContent = error.message; query('[data-activity-retry]').hidden = false; } }
    };
    const options = { signal: life.signal };
    root.addEventListener('click', event => {
      const winner = event.target.closest('[data-challenge-winner]');
      if (winner) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
        const contentId = winner.dataset.contentId;
        if (!contentId) return;
        event.preventDefault();
        detail.open(contentId, 'CHALLENGE', undefined, { returnTarget: winner });
        return;
      }
      const invite = event.target.closest('[data-challenge-invite]');
      if (invite) {
        event.preventDefault();
        if (invite.disabled) return;
        const image = query('.monthly-challenge__hero-background img');
        shareContent.openPage({
          url: challengeShareUrl(location.href), title: activity?.name || document.title,
          heading: 'Share this challenge',
          media: image ? [{ type: 'image', url: image.currentSrc || image.src }] : []
        }, invite);
        return;
      }
      const vote = event.target.closest('[data-challenge-vote-link]');
      if (!vote || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      event.preventDefault();
      if (root.classList.contains('monthly-challenge--detail')) detail.close();
      const url = new URL(location.href); url.searchParams.delete('content'); url.searchParams.delete('join_challenge'); url.hash = new URL(vote.href, location.href).hash;
      history.replaceState({}, '', url);
      const feed = query('.monthly-challenge__feed');
      feed?.focus({ preventScroll: true });
      feed?.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, options);
    root.addEventListener('animationend', event => {
      if (event.animationName === 'challenge-flip-bottom') event.target.classList.remove('is-flipping');
    }, options);
    reducedMotion.addEventListener('change', settleCountdownFlips, options);
    document.addEventListener('visibilitychange', () => { settleCountdownFlips(); if (document.hidden) stopCountdown(); else renderCountdown(); }, options);
    root.addEventListener('community:detail-view', () => root.classList.add('monthly-challenge--detail'), options);
    root.addEventListener('community:detail-close', () => root.classList.remove('monthly-challenge--detail'), options);
    mobile.addEventListener('change', () => { settleCountdownFlips(); page = 1; load(); }, options);
    selector.addEventListener('change', () => { detail.close(); const url = new URL(location.href); url.searchParams.delete('content'); history.replaceState({}, '', url); selectActivity(selector.value); }, options);
    query('[data-activity-retry]').addEventListener('click', () => selector.value ? selectActivity(selector.value) : start(), options);
    query('[data-challenge-retry]').addEventListener('click', load, options);
    root.querySelectorAll('[data-challenge-sort]').forEach(control => control.addEventListener('click', () => {
      sort = control.dataset.challengeSort; page = 1;
      root.querySelectorAll('[data-challenge-sort]').forEach(button => { button.classList.toggle('is-active', button === control); button.setAttribute('aria-pressed', String(button === control)); }); load();
    }, options));
    form.addEventListener('monthly-challenge:created', () => {
      form.dialog.close();
      window.publicComponents?.messageOpen({ content: form.config.messages.success, type: 'success', duration: 4200 });
      selectActivity(activity.id);
    }, options);
    form.dialog.addEventListener('close', () => { selector.disabled = !selector.options.length; }, options);
    form.dialog.addEventListener('toggle', () => { if (form.dialog.open) selector.disabled = true; }, options);
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true; stopCountdown(); settleCountdownFlips(); life.abort(); detail.destroy(); productCleanups.forEach(cleanup => cleanup()); delete root.dataset.challengeReady;
    }, options);
    const params = new URL(location.href).searchParams;
    const contentId = params.get('content');
    const startup = start({ loadCurrent: !contentId });
    if (contentId) detail.open(contentId, 'CHALLENGE');
    await startup;
    if (disposed) return;
    if (!contentId && params.get('join_challenge') === '1') await form.open(form.querySelector('[data-open]'));
  };
  const initAll = (scope = document) => scope.querySelectorAll('[data-monthly-challenge]').forEach(init);
  window.MonthlyChallenge = { initAll, canSubmit };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll()); else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
